import React, { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import { FONT_HEADING, FONT_UI } from "../layouts/constants";
import { getDeferredPrompt, subscribe, clearDeferredPrompt, wasInstalledThisSession } from "../utils/pwaInstall";

export default function InstallationModule() {
  const [deferredPrompt, setDeferredPrompt] = useState(() => getDeferredPrompt());
  const [isInstalled, setIsInstalled] = useState(() => wasInstalledThisSession());
  const [status, setStatus] = useState("Checking installation...");
  const [statusType, setStatusType] = useState("warning");

  const [checks, setChecks] = useState({
    https: "Checking...",
    manifest: "Checking...",
    serviceWorker: "Checking...",
    device: "Checking...",
  });

  // ===== UTIL =====
  const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isSafari = () =>
    /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  // ===== INIT =====
  useEffect(() => {
    checkPWAStatus();

    // Picks up an event that already fired before this component mounted
    // (e.g. user navigated here via client-side routing), and also listens
    // for one that fires later.
    const unsubscribe = subscribe(({ type }) => {
      if (type === "installed") {
        setIsInstalled(true);
        setDeferredPrompt(null);
        setStatus("App installed successfully!");
        setStatusType("success");
        toast.success("App installed successfully! \ud83c\udf89");
        return;
      }

      if (type === "available") {
        setDeferredPrompt(getDeferredPrompt());
        setStatus("Ready to install!");
        setStatusType("success");
        toast.success("App is ready to install!");
      }
    });

    return unsubscribe;
  }, []);

  // ===== STATUS CHECK =====
  const checkPWAStatus = async () => {
    // HTTPS
    const https =
      window.location.protocol === "https:" ||
      window.location.hostname === "localhost"
        ? "Secure \u2713"
        : "HTTPS Required";

    // Manifest
    let manifest = "Not Found";
    try {
      const res = await fetch("/manifest.json");
      manifest = res.ok ? "Found \u2713" : "Missing";
    } catch {
      manifest = "Error";
    }

    // Service Worker
    let sw = "Not Supported";
    if ("serviceWorker" in navigator) {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        sw = reg ? "Active \u2713" : "Not Registered";
      } catch {
        sw = "Error";
      }
    }

    // Device
    const mobile = /Android|iPhone|iPad/i.test(navigator.userAgent);
    const device = mobile ? "Mobile \u2713" : "Desktop";

    setChecks({ https, manifest, serviceWorker: sw, device });

    if (
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone
    ) {
      setIsInstalled(true);
      setStatus("Already Installed");
      setStatusType("success");
      toast.success("App is already installed!");
    }

    toast.success("PWA status checked successfully!");
  };

  // ===== INSTALL =====
  const handleInstall = async () => {
    if (isIOS() && isSafari()) {
      toast.error("Use Share \u2192 Add to Home Screen on iOS Safari");
      return;
    }

    const promptEvent = deferredPrompt || getDeferredPrompt();

    if (!promptEvent) {
      toast.error(
        "Install option not available yet. This can happen if the browser hasn't finished checking install requirements, or if this app is already installed. Try reloading this page directly."
      );
      return;
    }

    promptEvent.prompt();
    const choice = await promptEvent.userChoice;

    if (choice.outcome === "accepted") {
      setStatus("Installed successfully!");
      setStatusType("success");
      toast.success("App installed successfully! \ud83c\udf89");
    } else {
      setStatus("Installation cancelled");
      setStatusType("warning");
      toast.error("Installation was cancelled");
    }

    clearDeferredPrompt();
    setDeferredPrompt(null);
  };

  const showInstructions = () => {
    toast(
      <div>
        <strong>Installation Instructions:</strong>
        <ol className="mt-2 list-decimal list-inside text-left">
          <li>Click the Install button</li>
          <li>Add to Home Screen</li>
          <li>Launch App from Home Screen</li>
        </ol>
      </div>,
      { duration: 6000 }
    );
  };

  return (
    <Layout activePage="installation">
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
          success: {
            iconTheme: {
              primary: "#22c55e",
              secondary: "white",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "white",
            },
          },
        }}
      />

      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
          Installation Module
        </h1>
        <p className="text-[13px] text-[var(--text-muted)] m-0 mb-[22px]">
          Install your Security Patrol PWA application
        </p>

        {/* Status Alert - Now only shows as a backup if toast fails */}
        {status && (
          <div
            className={`mb-3.5 px-4 py-3 rounded-xl text-[13px] font-bold border ${
              statusType === "success"
                ? "bg-[rgba(34,197,94,0.12)] border-[rgba(34,197,94,0.28)] text-[#22c55e]"
                : statusType === "error"
                ? "bg-[var(--red-faint)] border-[var(--red-border)] text-[var(--red)]"
                : "bg-[rgba(245,158,11,0.12)] border-[rgba(245,158,11,0.28)] text-[#f59e0b]"
            }`}
            style={{ fontFamily: FONT_UI }}
          >
            {status}
          </div>
        )}

        {/* Top Card */}
        <div className="relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />
          <div className="flex items-center justify-between gap-3.5 flex-wrap">
            <div>
              <h2 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                PWA Status Check
              </h2>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Verify all PWA requirements are met for installation
              </p>
            </div>
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={checkPWAStatus}
                className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5"
              >
                Re-check
              </button>
              <button
                onClick={showInstructions}
                className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
                style={{
                  background: "var(--btn-bg)",
                  color: "var(--btn-text)",
                  boxShadow: "0 4px 18px var(--accent-glow)",
                }}
              >
                Instructions
              </button>
            </div>
          </div>
        </div>

        {/* Status Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-[18px]">
          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--btn-bg)] border border-[var(--surface-border)] shadow-[0_4px_18px_var(--accent-glow)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--btn-text)] opacity-75 m-0 mb-1.5">
              HTTPS
            </p>
            <p className="text-[16px] font-extrabold leading-none m-0 text-[var(--btn-text)]">
              {checks.https}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Manifest
            </p>
            <p className="text-[16px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {checks.manifest}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Service Worker
            </p>
            <p className="text-[16px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {checks.serviceWorker}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Device
            </p>
            <p className="text-[16px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {checks.device}
            </p>
          </div>
        </div>

        {/* Install Button Section */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0 mb-4">
            Install Application
          </h3>
          <p className="text-[13px] text-[var(--text-muted)] m-0 mb-5">
            Click the button below to install this app on your device
          </p>

          <button
            onClick={handleInstall}
            disabled={isInstalled}
            className={`border-0 rounded-xl px-5 py-3 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5 ${
              isInstalled
                ? "opacity-60 cursor-not-allowed hover:translate-y-0"
                : ""
            }`}
            style={{
              background: isInstalled ? "var(--surface)" : "var(--btn-bg)",
              color: isInstalled ? "var(--text-muted)" : "var(--btn-text)",
              boxShadow: isInstalled ? "none" : "0 4px 18px var(--accent-glow)",
            }}
          >
            {isInstalled ? "\u2705 Installed" : "\ud83d\udcf2 Install App"}
          </button>
        </div>

        {/* Features Section */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0 mb-4">
            Features
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Feature title="Offline Access" />
            <Feature title="Native Experience" />
            <Feature title="Push Notifications" />
            <Feature title="Home Screen App" />
            <Feature title="Auto Updates" />
            <Feature title="Cross Platform" />
          </div>
        </div>
      </div>
    </Layout>
  );
}

/* ================= UI COMPONENTS ================= */

function Feature({ title }) {
  return (
    <div className="border border-[var(--surface-border)] bg-[var(--surface)] rounded-xl p-4 text-center hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)] transition-colors">
      <div className="text-[14px] font-extrabold text-[var(--text-primary)]">
        {title}
      </div>
    </div>
  );
}